import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useMetricsUi, PillTabs } from "../../components/metrics/metricsUi";
import { formatDateTime } from "../../utils/formatDate";
import ForecastResultView from "./ForecastResultView";
import ProductArtifactInsight from "./ProductArtifactInsight";
import ProductArtifactSections from "./ProductArtifactSections";
import SkuOnDemandPanel from "./SkuOnDemandPanel";
import useForecastArtifact from "./useForecastArtifact";
import { productService } from "../../utils/services/productService";
import { inventoryService } from "../../utils/services/inventoryService";
import { forecastDataService } from "../../utils/services/forecast-data.service";

// Full SKU code — option data when the catalog has it, else the SKU as-is.
// `title` is deliberately not used — it isn't reliably just the variant name.
const variantLabel = (v) =>
  (v?.option || [])
    .map((o) => o.value)
    .filter(Boolean)
    .join(" / ") || v?.sku;

const productKey = (run) =>
  (run?.input?.product?._id || run?.input?.product || "").toString();
const productLabel = (run) =>
  run?.input?.product?.name || run?.sku || "Product";

// Every stored run is shown now, not just the latest per SKU, so the same
// SKU can legitimately appear more than once in one tab set. Bare "SKU-123"
// labels would then be indistinguishable — append the run's date only when
// that SKU actually repeats within `list`, so the common (single-run) case
// stays as clean as before.
const skuTabLabel = (list, run, i) => {
  const sku = run?.sku || `SKU ${i + 1}`;
  const isDupe =
    run?.sku && list.filter((r) => r?.sku === run.sku).length > 1;
  return isDupe && run?.createdAt
    ? `${sku} · ${formatDateTime(run.createdAt)}`
    : sku;
};

// Everything about "which SKU tab is effectively selected" for a product —
// shared by the top-level `activeRun`/`isAllSkusSelected` derivation and the
// render branch below, so they can never disagree about what's on screen.
const computeSkuView = ({ productGroups, pTab, sTab, generatedRuns, productSkuList }) => {
  if (!productGroups.length) {
    return {
      skuItems: [],
      effectiveSkuItems: [],
      missingSkus: [],
      showSkuTabs: false,
      sIdx: sTab,
      isMissingSelected: typeof sTab === "string",
    };
  }
  const pIdx = Math.min(pTab, productGroups.length - 1);
  const skuItems = productGroups[pIdx].items;

  // Show whatever's actually stored, however old — hiding real data behind
  // an empty "no forecast yet" panel just because it wasn't refreshed today
  // was worse than showing a possibly-stale number (the nightly top-seller
  // job only covers the top 50 products, so most SKUs would never qualify as
  // "fresh" under a 1-day cutoff and would permanently lose their real data
  // in the UI). Runs generated on-demand this session are merged in for any
  // SKU not already covered by a stored run.
  const effectiveSkuItems = [
    ...skuItems,
    ...Object.values(generatedRuns).filter(
      (r) => r && !skuItems.some((x) => x.sku === r.sku),
    ),
  ];

  // Product SKUs with no run at all (stored or session-generated) — each
  // still gets its own chip, landing on SkuOnDemandPanel instead of
  // being silently absent from the tab list.
  const presentSkus = new Set(
    effectiveSkuItems.map((r) => r.sku).filter(Boolean),
  );
  const missingSkus = productSkuList.filter(
    (v) => v.sku && !presentSkus.has(v.sku),
  );

  const isMissingSelected = typeof sTab === "string";

  // Show the sub-tab row whenever there's more than one thing to switch
  // between — including when the only "other" thing is a missing SKU's own
  // chip, so there's still a way to reach SkuOnDemandPanel for it.
  const showSkuTabs =
    effectiveSkuItems.length + missingSkus.length > 1 || missingSkus.length > 0;

  // -1 = product-level view.
  const sIdx = isMissingSelected
    ? sTab
    : showSkuTabs && sTab < 0
      ? -1
      : Math.max(0, Math.min(sTab, effectiveSkuItems.length - 1));

  return { skuItems, effectiveSkuItems, missingSkus, showSkuTabs, sIdx, isMissingSelected };
};

/**
 * Renders one or more forecast-data runs.
 *  - 1 run        → the result view, no tabs
 *  - Product mode → a tab per product, and SKU sub-tabs within each product
 *  - otherwise    → a flat tab per SKU (multi-SKU, Category, Product Type)
 *
 * `onActiveRunChange`, if given, is called with whichever run is currently
 * on screen — on mount and every time the tab selection changes — so a
 * parent showing other run-scoped UI alongside this (e.g. the Forecast
 * Parameters panel) can stay in sync with the tab the user actually picked,
 * instead of always reflecting the first run in `runs`.
 */
export default function ForecastResults({
  runs = [],
  onActiveRunChange,
  autoGenerateArtifact = false,
  // "kaman" for the planning pages; "demand_forecast" on the admin
  // Updated Forecast page — used for on-demand SKU runs below.
  engine = "kaman",
  // The Product Artifact (LLM report). Each engine has its own artifact,
  // so the Updated Forecast page shows the one built from its own runs.
  showArtifact = true,
  // Updated Forecast page: Summary + Method live in the Forecast Parameters panel.
  hideOverview = false,
}) {
  const ui = useMetricsUi();
  const { colors } = ui;

  const isProductMode = useMemo(
    () =>
      runs.some(
        (r) => String(r?.input?.forecast_by || "").toLowerCase() === "product",
      ),
    [runs],
  );

  // Product mode → group runs by product, preserving first-seen order.
  const productGroups = useMemo(() => {
    if (!isProductMode) return [];
    const map = new Map();
    runs.forEach((r) => {
      const key = productKey(r) || r.sku || Math.random().toString(36);
      if (!map.has(key)) {
        map.set(key, { key, name: productLabel(r), items: [] });
      }
      map.get(key).items.push(r);
    });
    return [...map.values()];
  }, [runs, isProductMode]);

  const [pTab, setPTab] = useState(0);
  // -1 = the product as a whole (all SKUs); >= 0 = that SKU's own tab.
  const [sTab, setSTab] = useState(-1);

  // The active product's real _id (not the group's tab `key`, which falls
  // back to a SKU or a random string when a run has no resolvable product).
  // Computed unconditionally, ahead of the early returns below, so the
  // useForecastArtifact call right after it obeys the Rules of Hooks; the
  // hook itself is a no-op whenever this is null (SKU/Category/ProductType
  // modes, or before any product tab is selected).
  const productId = useMemo(() => {
    if (!isProductMode || !productGroups.length) return null;
    const pIdx = Math.min(pTab, productGroups.length - 1);
    const skuItems = productGroups[pIdx]?.items || [];
    return (
      skuItems[0]?.input?.product?._id || skuItems[0]?.input?.product || null
    );
  }, [isProductMode, productGroups, pTab]);

  const activeClientId = useMemo(() => {
    if (!isProductMode || !productGroups.length) return null;
    const pIdx = Math.min(pTab, productGroups.length - 1);
    return productGroups[pIdx]?.items?.[0]?.client || null;
  }, [isProductMode, productGroups, pTab]);

  // Single source of truth for this product's artifact — shared by
  // ProductArtifactInsight (rendered before the chart) and
  // ProductArtifactSections (rendered after), so neither causes its own
  // fetch/subscribe/auto-generate.
  const { artifact: productArtifact, generate: generateProductArtifact } =
    useForecastArtifact(showArtifact ? productId : null, {
      autoGenerate: showArtifact && autoGenerateArtifact,
      // Each engine has its own artifact (see useForecastArtifact).
      engine: engine === "demand_forecast" ? engine : undefined,
      clientId: activeClientId ? String(activeClientId?._id || activeClientId) : undefined,
    });

  // The product's full SKU list (from its catalog variants, not just the
  // SKUs that happen to have a stored forecast run) — lets a SKU with no
  // run yet still get its own chip instead of being silently absent.
  const [productSkuList, setProductSkuList] = useState([]);
  useEffect(() => {
    if (!productId) {
      setProductSkuList([]);
      return undefined;
    }
    let cancelled = false;
    productService
      .get(productId)
      .then((product) => {
        if (cancelled) return;
        const list = (product?.variants || [])
          .filter((v) => v?.sku)
          .map((v) => ({ sku: v.sku, label: variantLabel(v) }));
        setProductSkuList(list);
      })
      .catch((e) => {
        console.error("Failed to load the product's SKU list", e);
        if (!cancelled) setProductSkuList([]);
      });
    return () => {
      cancelled = true;
    };
  }, [productId]);

  // Runs generated on the spot via SkuOnDemandPanel, keyed by SKU — merged
  // in wherever stored runs are used, so a just-generated SKU behaves
  // exactly like one that already had data (its own tab, joins the stacked
  // chart, etc.) without waiting for a re-fetch from the parent.
  const [generatedRuns, setGeneratedRuns] = useState({});

  // In-flight/failed on-demand generations, keyed by SKU — owned here
  // (not by SkuOnDemandPanel) so a generation survives the user switching to
  // another SKU tab and back: that panel unmounts/remounts with every tab
  // change, but this component doesn't, so "generating" (or an error) is
  // still shown correctly on return instead of resetting to the idle prompt.
  const [skuGeneration, setSkuGeneration] = useState({});

  // Reset tab positions (and locally-generated runs) whenever the run set
  // changes.
  useEffect(() => {
    setPTab(0);
    setSTab(-1);
    setGeneratedRuns({});
    setSkuGeneration({});
  }, [runs]);

  // Whichever run the current tab selection resolves to — mirrors the
  // branching below without duplicating it, so it stays correct through
  // every mode (single run, product/SKU tabs, flat SKU tabs).
  const skuView = useMemo(
    () =>
      isProductMode
        ? computeSkuView({ productGroups, pTab, sTab, generatedRuns, productSkuList })
        : null,
    [isProductMode, productGroups, pTab, sTab, generatedRuns, productSkuList],
  );

  const activeRun = useMemo(() => {
    if (!runs.length) return null;
    if (runs.length === 1) return runs[0];
    if (isProductMode && skuView) {
      // A "no data yet" (or stale) SKU chip is selected — nothing current
      // to show. Otherwise -1 ("All SKUs") falls back to the first fresh
      // SKU as a representative run for the Forecast Parameters panel.
      if (skuView.isMissingSelected) return null;
      const idx = skuView.sIdx === -1 ? 0 : skuView.sIdx;
      return skuView.effectiveSkuItems[idx] || null;
    }
    const idx = Math.min(pTab, runs.length - 1);
    return runs[idx];
  }, [runs, isProductMode, skuView, pTab]);

  // Whether "All SKUs" (the product as a whole, not one specific SKU) is
  // the active selection — the Forecast Parameters panel uses this to hide
  // the SKU field, which would otherwise show one arbitrary SKU's code
  // while every other part of the page is displaying the whole product.
  const isAllSkusSelected = Boolean(isProductMode && skuView?.sIdx === -1);

  useEffect(() => {
    onActiveRunChange?.(activeRun, { isAllSkus: isAllSkusSelected });
  }, [activeRun, isAllSkusSelected, onActiveRunChange]);

  // If the SKU the user is currently looking at (a "missing:<sku>" tab)
  // just finished generating — without them ever switching away — move
  // `sTab` onto its real numeric index so the view updates to the actual
  // result immediately, instead of staying on the stale "not generated yet"
  // panel until they navigate away and back.
  useEffect(() => {
    if (typeof sTab !== "string" || !isProductMode || !skuView) return;
    const sku = sTab.slice("missing:".length);
    if (!generatedRuns[sku]) return;
    const i = skuView.effectiveSkuItems.findIndex((r) => r.sku === sku);
    if (i >= 0) setSTab(i);
  }, [sTab, generatedRuns, isProductMode, skuView]);

  if (!runs.length) return null;
  if (runs.length === 1) return <ForecastResultView run={runs[0]} hideOverview={hideOverview} />;

  const caption = (
    <Typography sx={{ fontSize: 12, color: colors.textSecondary, mb: 1.25 }}>
      {runs.length} forecast results
    </Typography>
  );

  // ── Product mode: product tabs → SKU sub-tabs ──
  if (isProductMode && productGroups.length && skuView) {
    const pIdx = Math.min(pTab, productGroups.length - 1);
    const {
      skuItems,
      effectiveSkuItems,
      missingSkus,
      showSkuTabs,
      sIdx,
      isMissingSelected,
    } = skuView;

    const selectedMissingSku = isMissingSelected
      ? sTab.slice("missing:".length)
      : null;
    const selectedMissingLabel =
      missingSkus.find((v) => v.sku === selectedMissingSku)?.label ||
      selectedMissingSku;

    // One run per SKU for the stacked chart — the same SKU can appear more
    // than once here (older stored runs), which would otherwise be counted
    // twice. Newest wins, except the SKU tab currently selected shows its own
    // run.
    const bySku = new Map();
    effectiveSkuItems.forEach((r) => {
      const k = r?.sku || r?._id;
      if (!bySku.has(k) || r === activeRun) bySku.set(k, r);
    });
    const stackRuns = [...bySku.values()];

    // Any sibling SKU's params from this product's most recent batch, so an
    // on-demand run stays comparable to the rest of the product instead of
    // falling back to bare defaults.
    const templateInput = skuItems[0]?.input || null;

    // Runs an on-demand forecast for one SKU with no stored run yet. State
    // lives on this component (see `skuGeneration` above), not inside
    // SkuOnDemandPanel, so it's unaffected by that panel unmounting when the
    // user switches to another SKU tab mid-generation.
    const generateSkuForecast = async (sku) => {
      setSkuGeneration((prev) => ({ ...prev, [sku]: { status: "generating" } }));
      try {
        const t = templateInput || {};
        const item = {
          sku,
          product: productId,
          horizon: Number(t.horizon) || 6,
          type: t.type || "Monthly",
          ...(Array.isArray(t.warehouse) && t.warehouse.length
            ? { warehouse: t.warehouse }
            : {}),
          ...(Array.isArray(t.channel) && t.channel.length
            ? { channel: t.channel }
            : {}),
          discount: t.discount || 0,
          forecast_by: "SKU",
          client: activeClientId,
          lead_time_days: t.lead_time_days ?? 0,
          safety_stock_percentage: t.safety_stock_percentage ?? 0,
          ...(t.min_coverage_days != null
            ? { min_coverage_days: t.min_coverage_days }
            : {}),
          ...(t.min_order_qty != null ? { min_order_qty: t.min_order_qty } : {}),
        };
        const payload = {
          items: [item],
          bulk: true,
          forecast_type: t.forecast_type || "warehouse",
          engine,
        };
        const res = await inventoryService.bulkForecast(payload);
        const result = (Array.isArray(res) ? res : [])[0];
        if (!result || result.error || !result.data?.forecast_data_id) {
          throw new Error(
            result?.message ||
              result?.error?.message ||
              "Forecast generation failed.",
          );
        }
        const doc = (
          await forecastDataService.get(result.data.forecast_data_id)
        )?.data;
        if (!doc) {
          throw new Error(
            "The forecast finished but its result couldn't be loaded.",
          );
        }
        setGeneratedRuns((prev) => ({ ...prev, [sku]: doc }));
        setSkuGeneration((prev) => {
          const next = { ...prev };
          delete next[sku];
          return next;
        });
      } catch (e) {
        console.error(`Failed to generate forecast for ${sku}`, e);
        setSkuGeneration((prev) => ({
          ...prev,
          [sku]: {
            status: "error",
            error:
              e?.response?.data?.message ||
              e?.message ||
              "Couldn't generate the forecast. Please try again.",
          },
        }));
      }
    };

    return (
      <Box>
        {/* {caption} */}
        <Box sx={{ mb: 1.25 }}>
          <PillTabs
            tabs={productGroups.map((g, i) => ({
              value: i,
              label: g.name,
              badge: g.items.length > 1 ? g.items.length : undefined,
            }))}
            value={pIdx}
            onChange={(v) => {
              setPTab(v);
              setSTab(-1);
            }}
          />
        </Box>

        {showSkuTabs && (
          <Box
            sx={{
              mb: 1.5,
              pb: 1.25,
              borderBottom: `1px solid ${colors.border}`,
            }}
          >
            <PillTabs
              tabs={[
                { value: -1, label: "All SKUs" },
                ...effectiveSkuItems.map((it, i) => ({
                  value: i,
                  label: skuTabLabel(effectiveSkuItems, it, i),
                })),
                ...missingSkus.map((v) => ({
                  value: `missing:${v.sku}`,
                  label: v.label,
                  tooltip: "No current forecast — click to generate",
                })),
              ]}
              value={sIdx}
              onChange={setSTab}
              scrollable
            />
          </Box>
        )}

        {showArtifact && productId && sIdx === -1 && (
          <Box sx={{ mb: 2 }}>
            <ProductArtifactInsight artifact={productArtifact} />
          </Box>
        )}

        {/* "All SKUs" → stacked chart of the whole product. A SKU tab with
            data → that SKU's own chart and details. A SKU tab with no run
            yet → the on-demand generate panel. */}
        {isMissingSelected ? (
          <SkuOnDemandPanel
            sku={selectedMissingSku}
            label={selectedMissingLabel}
            status={skuGeneration[selectedMissingSku]?.status || "idle"}
            errorMessage={skuGeneration[selectedMissingSku]?.error || ""}
            onGenerate={() => generateSkuForecast(selectedMissingSku)}
          />
        ) : (
          <ForecastResultView
            run={activeRun}
            hideOverview={hideOverview}
            stackRuns={sIdx === -1 ? stackRuns : undefined}
            chartOnly={sIdx === -1}
            onSelectRun={(r) => {
              const i = effectiveSkuItems.indexOf(r);
              if (i >= 0) setSTab(i);
            }}
          />
        )}

        {/* Product-level report (Insight / Key Issues / Recommendations) —
            only for the "All SKUs" view. A specific SKU tab shows just its
            own forecast data, the same as before this feature existed. */}
        {showArtifact && productId && sIdx === -1 && (
          <Box sx={{ mt: 2 }}>
            <ProductArtifactSections
              artifact={productArtifact}
              generate={generateProductArtifact}
            />
          </Box>
        )}
      </Box>
    );
  }

  // ── Flat SKU tabs (multi-SKU / Category / Product Type) ──
  const idx = Math.min(pTab, runs.length - 1);
  return (
    <Box>
      {/* {caption} */}
      <Box
        sx={{ mb: 1.5, pb: 1.25, borderBottom: `1px solid ${colors.border}` }}
      >
        <PillTabs
          tabs={runs.map((r, i) => ({
            value: i,
            label: skuTabLabel(runs, r, i),
          }))}
          value={idx}
          onChange={setPTab}
          scrollable
        />
      </Box>
      <ForecastResultView run={activeRun} hideOverview={hideOverview} />
    </Box>
  );
}
